import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/utils/services/dialog.service';
import { DataTableDirective } from 'angular-datatables';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { environment } from 'src/environments/environment';
import { DocumenteCetateanService } from './documente-cetatean.service';
import { helper } from 'src/environments/helper';
import { AddDocumentCetateanComponent } from './add-document-cetatean/add-document-cetatean.component';
import { FormGroup, FormControl } from '@angular/forms';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';
import { CommonService } from 'src/app/services/common.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AppFilesService } from 'src/app/services/app-files.service';
import { EditDocumentCetateanComponent } from './edit-document-cetatean/edit-document-cetatean.component';
import { EditFisiereDocumentCetateanComponent } from './edit-fisiere-document-cetatean/edit-fisiere-document-cetatean.component';
import { IstoricDocumentCetateanComponent } from './istoric-document-cetatean/istoric-document-cetatean.component';

@Component({
    selector: 'app-documente-cetatean',
    templateUrl: './documente-cetatean.component.html',
    styleUrls: ['./documente-cetatean.component.scss']
})
export class DocumenteCetateanComponent {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    isLoading = false;
    isFilterLoading = false;
    showCard = false;

    helper = helper;

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    directiiActive: Generic[] = [];
    compartimenteActive!: Generic[];
    activeUsers!: Generic[];

    private destroy$ = new Subject<void>();

    // Filtered fields for search
    filteredDirectii!: any[];
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);
        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    dateFilteringOptions: { id: number; value: string }[] = [
        { id: 1, value: 'Adăugare' },
        { id: 2, value: 'Arhivare' },
        { id: 3, value: 'Validare' },
        { id: 4, value: 'Anulare' },
    ];

    dateFilterOption: number = 1;

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    archivedParameters: { key: string; value: number }[] = [
        { key: 'Da', value: 1 },
        { key: 'Nu', value: 0 },
    ];

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);
        this.onDirectieChange(-1);

        this.filteredDirectii = this.directiiActive;
        this.directiiControl.setValue(null);

        this.compartimenteControl.setValue(null);

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch();

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    searchParamsForm!: FormGroup;

    initialFormValues = {
        directie_id: -1,
        compartiment_id: -1,
        operator_id: '-1',
        archive: 0,
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            directie_id: new FormControl(-1),
            compartiment_id: new FormControl(-1),
            operator_id: new FormControl('-1'),
            archive: new FormControl(0),
        });
    }

    dtLink = environment.interop.basePath + environment.interop.platform.nomenclatoare.documenteCetatean.getDocumenteCetateanDT;

    constructor(
        private http: HttpClient,
        private documenteCetateanService: DocumenteCetateanService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        public permissionsService: PermissionsService,
        private commonService: CommonService,
        private breakpointObserver: BreakpointObserver,
        private appFilesService: AppFilesService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.loadDirectiiActive();
        this.loadActiveUsers();
        this.renderFieldsSearch();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    renderDT() {
        this.isLoading = true;
        this.dtOptions = {
            order: [[0, 'desc']],
            serverSide: true,
            searching: false,
            responsive: true,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.filters = {
                    ...this.searchParamsForm.value,
                    date_type: +this.dateFilterOption
                };
                dataTablesParameters.date_from = this.formatDate(
                    this.selectedStartDate
                );
                dataTablesParameters.date_to = this.formatDate(this.selectedEndDate);

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID Document',
                    data: 'id_document',
                },
                {
                    title: 'ID Cetățean',
                    data: null,
                    orderable: false,
                    render: (row: any) => {
                        let cnp_cif;

                        if (row.f_nume === '') {
                            cnp_cif = 'CNP';
                        } else {
                            cnp_cif = 'CIF';
                        }

                        return `
                            <div class="d-flex flex-column">
                                ` + (row.f_nume !== '' ? `<span class="d-flex align-items-center gap-1"><img src="/assets/icons/companii.svg" alt="Image" style="width: 20px; height: 20px;">${row.f_nume}</span>` : '') + `
                                <span class="d-flex align-items-center gap-1"><img src="/assets/icons/icon-user.svg" alt="Image" style="width: 20px; height: 20px;">${row.citizen_name}</span>
                                <span>${cnp_cif}: ${row.cnp_cif}</span>
                                ` + (row.notificare_sms_allow == true ? `<span class="d-flex align-items-center gap-1"><i class="fa-solid fa-phone"></i> ${row.notificare_sms_number}</span>` : '') + `
                                ` + (row.notificare_email_allow == true ? `<span class="d-flex align-items-center gap-1"><i class="fa-regular fa-envelope"></i> ${row.notificare_email_address}</span>` : '') + `
                            </div>
                        `;
                    }
                },
                {
                    title: 'Tip Document',
                    data: 'document_type_name',
                    orderable: false,
                },
                {
                    title: 'Data încărcării',
                    data: 'uploaded_date',
                    render: (data: any) => {
                        return this.formatRomanianDate(data);
                    }
                },
                {
                    title: 'Fișiere listate',
                    data: 'files',
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        const icon = '<img src="assets/icons/icon-download.svg" alt="Icon download" />';
                        return row.map((file: any) => `<span style="cursor: pointer;" class="d-flex gap-2 align-items-center file-link" data-id-fisier="${file.id_fisier}">${icon} <span style="color: #0044CC99;">${file.orig_name}</span></span></span>`).join('<br>');
                    }
                },
                {
                    title: 'Data arhivării',
                    data: null,
                    render: (row: any) => {
                        if (row.archive_date !== null) {
                            return `<span>${this.formatRomanianDate(row.archive_date)}</span>`;
                        } else {
                            return `<span>N/A</span>`;
                        }
                    }
                },
                {
                    title: 'Data actualizării',
                    data: 'updated_at',
                    render: (data: any) => {
                        return this.formatRomanianDate(data);
                    }
                },
                {
                    title: 'Operator Validare',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        let validare = ` 
                                            <button class="validate btn btn-success ms-1 d-flex align-items-center gap-1" style="border-radius: 4px">
                                                <i class="fa-regular fa-file"></i>Confirmare
                                            </button>
                                        `;

                        let resetValidare = ` 
                                                <button class="resetValidate btn btn-warning ms-1" style="border-radius: 4px">
                                                    RESET
                                                </button>
                                            `;

                        let cancel = ` 
                                        <button class="cancel btn btn-danger ms-1 d-flex align-items-center gap-1" style="border-radius: 4px">
                                            <i class="fa-solid fa-xmark"></i>Anulare
                                        </button>
                                    `;

                        let resetCancel = ` 
                                            <button class="resetCancel btn btn-warning ms-1" style="border-radius: 4px">
                                                RESET
                                            </button>
                                        `;

                        if (row.cancel_date === null) {
                            if (row.validated_date === null) {
                                return `
                                            <div class="d-flex flex-column justify-content-left">
                                                <div class="d-flex justify-content-center">
                                                    ${validare}
                                                </div>
                                                <hr>
                                                <div class="d-flex justify-content-center">
                                                    ${cancel}
                                                </div>
                                            </div>
                                        `
                            } else {
                                return `
                                            <div class="d-flex flex-column justify-content-left">
                                                <div class="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <div class="d-flex align-items-center">
                                                            <i class="fa-regular fa-user me-2"></i>        
                                                            <span>${row.validated_user_name}</span>
                                                        </div>
                                                        <div class="d-flex align-items-center">
                                                            <i class="fa-regular fa-clock me-2"></i>        
                                                            <span>${this.formatRomanianDate(row.validated_date)}</span>
                                                        </div>
                                                        ` + (row.validated_obs !== null ?
                                        `<div class="d-flex align-items-center">
                                                                <i class="fa-regular fa-comments me-2"></i>       
                                                                <span>${row.validated_obs}</span>
                                                             </div>    
                                                        ` : '') + `
                                                    </div>
                                                    ${resetValidare}
                                                </div>
                                                <hr>
                                                <div class="d-flex align-center justify-content-center">
                                                    ${cancel}
                                                </div>
                                            </div>
                                        `
                            }
                        } else {
                            return `
                                    <div class="d-flex flex-column justify-content-left">
                                        ` + (row.validated_date !== null ? `
                                            <div class="d-flex align-items-center justify-content-start">
                                            <div>
                                                <div class="d-flex align-items-center">
                                                    <i class="fa-regular fa-user me-2"></i>       
                                                    <span>${row.validated_user_name}</span>
                                                </div>
                                                <div class="d-flex align-items-center">
                                                    <i class="fa-regular fa-clock me-2"></i>        
                                                    <span>${this.formatRomanianDate(row.validated_date)}</span>
                                                </div>
                                                ` + (row.validated_obs !== null ?
                                        `<div class="d-flex align-items-center">
                                                        <i class="fa-regular fa-comments me-2"></i>       
                                                        <span>${row.validated_obs}</span>
                                                        </div>    
                                                ` : '') + `
                                            </div>
                                        </div>
                                        <hr>
                                            ` : '') + `
                
                                        <div class="d-flex align-items-center justify-content-between">
                                            <div>
                                                <div class="d-flex align-items-center">
                                                    <i class="fa-solid fa-xmark me-2 red-text"></i>          
                                                    <span>${row.cancel_user_name}</span>
                                                </div>
                                                <div class="d-flex align-items-center">
                                                    <i class="fa-regular fa-clock me-2 red-text"></i>        
                                                    <span>${this.formatRomanianDate(row.cancel_date)}</span>
                                                </div>
                                                ` + (row.cancel_obs !== null ?
                                    `<div class="d-flex align-items-center">
                                                        <i class="fa-regular fa-comments me-2 red-text"></i>       
                                                        <span>${row.cancel_obs}</span>
                                                        </div>    
                                                ` : '') + `
                                            </div>
                                            ${resetCancel}
                                        </div>
                                    </div>
                                `
                        }
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    className: 'all',
                    orderable: false,
                    render: () => {
                        return `
                            <div class="d-flex align-center justify-content-left">
                                <button class="action-btn edit ms-1" title="Editare informații document">
                                    <img class="btn-img" src="../../../../assets/icons/edit.svg" alt="edit"/>
                                </button>
                                <button class="action-btn signatures ms-1" title="Editare fișiere document">
                                    <i class="fa-regular fa-file fa-sm" style="color: #ffffff;"></i>
                                </button>
                                <button class="action-btn historic ms-1" title="Istoric document">
                                    <i class="fa-solid fa-clock-rotate-left fa-sm" style="color: #ffffff;"></i>
                                </button>
                            </div>
                        `;
                    }
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const editIcon = lastTd?.querySelector('.edit') as HTMLElement;
                const editFilesIcon = lastTd?.querySelector('.signatures') as HTMLElement;
                const historyIcon = lastTd?.querySelector('.historic') as HTMLElement;

                const penultimateTd = (row as HTMLElement).querySelector('td:nth-last-child(2)');
                const validateButton = penultimateTd?.querySelector('.validate') as HTMLElement;
                const resetValidateButton = penultimateTd?.querySelector('.resetValidate') as HTMLElement;
                const cancelButton = penultimateTd?.querySelector('.cancel') as HTMLElement;
                const resetCancelButton = penultimateTd?.querySelector('.resetCancel') as HTMLElement;

                const fileLinks = (row as HTMLElement).querySelectorAll('.file-link');
                fileLinks.forEach(fileLink => {
                    fileLink.addEventListener('click', () => {
                        const { id_document } = data;
                        const id_fisier = parseInt(fileLink.getAttribute('data-id-fisier') || '0', 10);
                        this.downloadDocument(id_document, id_fisier);
                    });
                });

                if (editIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.update'
                        )
                    ) {
                        editIcon.style.display = 'none';
                    }
                    editIcon.addEventListener('click', () => {
                        this.openEditDialog(data);
                    });
                }

                if (editFilesIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.update'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }
                    editFilesIcon.addEventListener('click', () => {
                        this.openEditFilesDialog(data);
                    });
                }

                if (historyIcon) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.history'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }
                    historyIcon.addEventListener('click', () => {
                        this.openHistoryDialog(data.id_document);
                    });
                }

                if (validateButton) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.validare'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }
                    validateButton.addEventListener('click', () => {
                        const { id_document } = data;
                        this.validateDocCetatean(id_document);
                    });
                }

                if (resetValidateButton) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.reset-validare'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }

                    resetValidateButton.addEventListener('click', () => {
                        const { id_document } = data;
                        this.resetValidateDocCetatean(id_document);
                    });
                }

                if (cancelButton) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.cancel'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }
                    cancelButton.addEventListener('click', () => {
                        const { id_document } = data;
                        this.cancelDocCetatean(id_document);
                    });
                }

                if (resetCancelButton) {
                    if (
                        !this.permissionsService.hasPermission(
                            'auth-module.solicitari.citizen-documents.reset-cancel'
                        )
                    ) {
                        editFilesIcon.style.display = 'none';
                    }

                    resetCancelButton.addEventListener('click', () => {
                        const { id_document } = data;
                        this.resetCancelDocCetatean(id_document);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    openAddDialog() {
        let configData = {
            width: '80%',
            height: 'auto',
            minWidth: '275px',
            autoFocus: false,
        };

        const dialogRef = this.dialog.open(AddDocumentCetateanComponent, configData);

        const layoutChanges = this.breakpointObserver.observe([
            '(max-width: 599px)', // xs breakpoint
            '(min-width: 600px)'  // sm breakpoint
        ]);

        const layoutChangeSubscription = layoutChanges.subscribe(result => {
            if (result.matches) {
                if (result.breakpoints['(max-width: 899px)']) {
                    // Smaller devices          
                    dialogRef.updateSize('95%', 'auto')
                } else {
                    // Larger devices
                    dialogRef.updateSize('90%', 'auto')
                }
            }
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Document adăugat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
            layoutChangeSubscription.unsubscribe();
        });
    }

    openEditDialog(data: any) {
        let configData = {
            width: '80%',
            height: 'auto',
            minWidth: '275px',
            autoFocus: false,
            data
        };

        const dialogRef = this.dialog.open(EditDocumentCetateanComponent, configData);

        const layoutChanges = this.breakpointObserver.observe([
            '(max-width: 599px)', // xs breakpoint
            '(min-width: 600px)'  // sm breakpoint
        ]);

        const layoutChangeSubscription = layoutChanges.subscribe(result => {
            if (result.matches) {
                if (result.breakpoints['(max-width: 899px)']) {
                    // Smaller devices          
                    dialogRef.updateSize('95%', 'auto')
                } else {
                    // Larger devices
                    dialogRef.updateSize('90%', 'auto')
                }
            }
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Document editat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
            layoutChangeSubscription.unsubscribe();
        });
    }

    openEditFilesDialog(data: any) {
        let configData = {
            width: '80%',
            height: 'auto',
            minWidth: '275px',
            autoFocus: false,
            data
        };

        const dialogRef = this.dialog.open(EditFisiereDocumentCetateanComponent, configData);

        const layoutChanges = this.breakpointObserver.observe([
            '(max-width: 599px)', // xs breakpoint
            '(min-width: 600px)'  // sm breakpoint
        ]);

        const layoutChangeSubscription = layoutChanges.subscribe(result => {
            if (result.matches) {
                if (result.breakpoints['(max-width: 899px)']) {
                    // Smaller devices          
                    dialogRef.updateSize('95%', 'auto')
                } else {
                    // Larger devices
                    dialogRef.updateSize('90%', 'auto')
                }
            }
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.isLoading = true;
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Document editat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
            layoutChangeSubscription.unsubscribe();
        });
    }

    openHistoryDialog(data: any) {
        let configData = {
            width: '80%',
            height: 'auto',
            minWidth: '275px',
            autoFocus: false,
            data
        };

        const dialogRef = this.dialog.open(IstoricDocumentCetateanComponent, configData);

        const layoutChanges = this.breakpointObserver.observe([
            '(max-width: 599px)', // xs breakpoint
            '(min-width: 600px)'  // sm breakpoint
        ]);

        const layoutChangeSubscription = layoutChanges.subscribe(result => {
            if (result.matches) {
                if (result.breakpoints['(max-width: 899px)']) {
                    // Smaller devices          
                    dialogRef.updateSize('95%', 'auto')
                } else {
                    // Larger devices
                    dialogRef.updateSize('90%', 'auto')
                }
            }
        });

        dialogRef.afterClosed().subscribe(() => {
            layoutChangeSubscription.unsubscribe();
        });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isFilterLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => (this.isFilterLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.directiiActive = results;
                    this.filteredDirectii = results;
                },
                error: (error) => {
                    console.error('Error loading directii active', error);
                    this.isFilterLoading = false;
                },
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(categorie =>
                categorie.id === this.searchParamsForm.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.searchParamsForm.controls['compartiment_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isFilterLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isFilterLoading = false;
            })
        ).subscribe({
            next: (compartimente) => {
                this.compartimenteActive = [...compartimente];
                this.filteredCompartimente = [...compartimente];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            }
        });
    }

    onCompartimenteSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.searchParamsForm.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        ));
    }

    loadActiveUsers() {
        this.isFilterLoading = true;

        this.commonService
            .getActiveUsers()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isFilterLoading = false))
            )
            .subscribe({
                next: (results) => {
                    this.activeUsers = results;
                },
                error: (error) => {
                    console.error('Error loading active users', error);
                    this.isFilterLoading = false;
                },
            });
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    onDateFilterChange(): void {
        this.rerenderDT();
    }

    formatRomanianDate(dateTimeString: string): string {
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year} ${timePart}`;
    }

    validateDocCetatean(id: number) {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: 'O să validați acest document!',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText:
                this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText:
                this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            input: 'text', // Enable text input
            inputPlaceholder: 'Completați motivul validării documentului.',
            preConfirm: (inputValue) => {
                return this.documenteCetateanService
                    .validateDocumentCetatean(id, inputValue)
                    .toPromise()
                    .then((response) => {
                        return response;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Documentul a fost validat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                })
            }
        }).then(() => {
            this.rerenderDT();
        });
    }

    resetValidateDocCetatean(id: number) {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: 'Sunteți sigur că doriți resetarea validării de document?',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText:
                this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText:
                this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            input: 'text', // Enable text input
            inputPlaceholder: 'Completați motivul resetării validării documentului.',
            inputValidator: (value: any) => {
                if (!value) {
                    return 'Este necesar completarea unui motiv!';
                }
                return null; // Return null if input is valid
            },
            preConfirm: (inputValue) => {
                if (!inputValue) {
                    Swal.showValidationMessage('Este necesar completarea unui motiv!');
                }

                return this.documenteCetateanService
                    .resetValidateDocumentCetatean(id, { observatii: inputValue })
                    .toPromise()
                    .then((response) => {
                        return response;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Documentul a fost resetat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.rerenderDT();
                });
            }
        });
    }

    cancelDocCetatean(id: number) {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: 'O să anulați acest document!',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText:
                this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText:
                this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            input: 'text', // Enable text input
            inputPlaceholder: 'Completați motivul anulării documentului.',
            inputValidator: (value) => {
                if (!value) {
                    return 'Este necesar completarea unui motiv!';
                }
                return null;
            },
            preConfirm: (inputValue) => {
                if (!inputValue) {
                    Swal.showValidationMessage('Este necesar completarea unui motiv!');
                }

                return this.documenteCetateanService
                    .cancelDocumentCetatean(id, inputValue)
                    .toPromise()
                    .then((response) => {
                        return response;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Documentul a fost anulat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.rerenderDT();
                });
            }
        })
    }

    resetCancelDocCetatean(id: number) {
        Swal.fire({
            title: this.helper.swalTextConfig.commonTitles.areYouSure,
            text: 'Sunteți sigur că doriți resetarea anulării de document?',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText:
                this.helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText:
                this.helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            input: 'text', // Enable text input
            inputPlaceholder: 'Completați motivul resetării anulării documentului.',
            inputValidator: (value: any) => {
                if (!value) {
                    return 'Este necesar completarea unui motiv!';
                }
                return null; // Return null if input is valid
            },
            preConfirm: (inputValue) => {
                if (!inputValue) {
                    Swal.showValidationMessage('Este necesar completarea unui motiv!');
                }

                return this.documenteCetateanService
                    .resetCancelDocumentCetatean(id, { observatii: inputValue })
                    .toPromise()
                    .then((response) => {
                        return response;
                    })
                    .catch((error) => {
                        Swal.showValidationMessage(`Request failed: ${error}`);
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Documentul a fost resetat cu succes!',
                    icon: 'success',
                    confirmButtonText: this.helper.swalTextConfig.buttonLabels.close,
                }).then(() => {
                    this.rerenderDT();
                });
            }
        });
    }

    downloadDocument(idDocument: number, idFisier: number) {
        let fullPath = this.documenteCetateanService.fileDownloadUrl + idDocument + '/' + idFisier;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }
}
